import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";
import { safeStorage } from "@/lib/safeStorage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NOTE: We rely on Bearer token in Authorization header, NOT cookies.
// Do NOT set axios.defaults.withCredentials = true — the ingress returns
// Access-Control-Allow-Origin: * which browsers block when credentials are used.

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => safeStorage.getItem("meos_token"));

  useEffect(() => {
    // Google OAuth callback: session_id fragment
    const handleGoogleCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes("session_id=")) {
        const params = new URLSearchParams(hash.slice(1));
        const sessionId = params.get("session_id");
        if (sessionId) {
          try {
            const { data } = await axios.post(`${API}/auth/session`, null, {
              headers: { "X-Session-ID": sessionId },
            });
            safeStorage.setItem("meos_token", data.session_token);
            setToken(data.session_token);
            setUser(data.user);
            window.history.replaceState(null, "", window.location.pathname);
            setLoading(false);
            return true;
          } catch (err) {
            console.error("Google auth failed", err);
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 403 && detail) {
              // Preserve message on URL so Login page can show it
              window.history.replaceState(null, "", `/login?pending=1&msg=${encodeURIComponent(detail)}`);
              window.location.reload();
            }
          }
        }
      }
      return false;
    };

    // External SSO: a trusted site (e.g. your Hostinger landing) can bounce the
    // user here with #token=<jwt> after calling /api/auth/login server-side.
    const handleExternalToken = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes("token=")) {
        const params = new URLSearchParams(hash.slice(1));
        const t = params.get("token");
        if (t) {
          try {
            const { data } = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${t}` },
            });
            safeStorage.setItem("meos_token", t);
            setToken(t);
            setUser(data);
            window.history.replaceState(null, "", window.location.pathname);
            setLoading(false);
            return true;
          } catch (err) {
            console.warn("External SSO token invalid", err?.response?.status || err?.message);
          }
        }
      }
      return false;
    };

    (async () => {
      const handled = await handleGoogleCallback();
      if (handled) return;
      const externalHandled = await handleExternalToken();
      if (externalHandled) return;
      if (!token) { setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(data);
      } catch (err) {
        console.warn("auth/me failed, clearing token", err?.response?.status || err?.message);
        safeStorage.removeItem("meos_token");
        setToken(null);
      }
      setLoading(false);
    })();
  }, [token]);

  const login = async (identifier, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { identifier, password });
    localStorage.setItem("meos_token", data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      // Logout endpoint failure must not block local cleanup
      console.warn("auth/logout failed:", err?.response?.status || err?.message);
    }
    localStorage.removeItem("meos_token");
    setToken(null);
    setUser(null);
  };

  // Memoize the axios instance so children's useEffect(..., [api]) don't re-run on every render
  const api = useMemo(() => axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }), [token]);

  // Memoize the context value so consumers don't re-render on unrelated parent updates
  const value = useMemo(
    () => ({ user, loading, login, logout, api, token }),
    [user, loading, api, token]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { API };
